import { useSession } from 'next-auth/react'

import type { Address, Order, Subscription } from '@rechargeapps/storefront-client'
import * as Sentry from '@sentry/nextjs'
import useSWR, { mutate as globalMutate } from 'swr'

import { getBasicShopifyOrderFront } from '@lib/operations/orders'
import {
	getLastCharge,
	getSubscriptions as getRCSubscriptions,
	getSubscriptionOrders,
	loginWithShopify
} from '@lib/operations/recharge'
import type { ShopifyBasicOrder } from '@lib/queries/orders/get-basic-orders-query'

/**
 * Type of customization based on the number of orders
 * - NMC: New Member Customization (first order)
 * - RMC: Returning Member Customization (more than one order)
 * - NONE: No orders yet
 */
export type CustomizationType = 'NMC' | 'RMC' | 'NONE'

/**
 * Extended subscription type that includes additional details about orders and status
 */
export type SubscriptionWithDetails = Subscription & {
	/** Shipping address associated with the subscription */
	address: Address
	/** Current status of the subscription */
	status: 'active' | 'cancelled' | 'expired'
	/** Total number of orders for this subscription */
	numOrders: number
	/** List of all orders associated with this subscription */
	orders?: Order[]
	/** Most recent box/order for this subscription */
	lastBox?: ShopifyBasicOrder | null
	/** Type of customization based on order count (NMC: first order, RMC: returning customer) */
	customizationType: CustomizationType
	/** Whether the latest order is a dunning recovery (failed charges preceded it) */
	isDunning: boolean
	/** Function to revalidate just this subscription */
	revalidate: () => Promise<void>
	/** Whether this is a prepaid cancelled subscription that still has pending orders */
	isEndingSoon?: boolean
	/** Number of remaining boxes in the subscription */
	remainingBoxes?: number
}

/**
 * Data structure returned by the useSubscriptions hook
 */
export type SubscriptionsData = {
	/** Array of all subscriptions with their details */
	subscriptions: SubscriptionWithDetails[] | null
}

/**
 * Processes a single subscription's orders and details
 *
 * @param rechargeSession - The active Recharge session
 * @param subscription - The base subscription data
 * @param accessToken - The Shopify access token for authentication
 * @returns The processed subscription details
 */
async function processSubscriptionDetails(
	rechargeSession: any,
	subscription: Subscription & { address: Address },
	accessToken: string
): Promise<SubscriptionWithDetails> {
	// Get orders for this subscription
	const subscriptionOrders = await getSubscriptionOrders(
		rechargeSession,
		subscription.id,
		undefined,
		'success'
	)

	// Get the last box from Shopify if there are orders
	let lastBox: ShopifyBasicOrder | null = null
	if (subscriptionOrders && subscriptionOrders.length > 0) {
		const shopifyOrderId = subscriptionOrders[0].external_order_id?.ecommerce
		if (shopifyOrderId) {
			const shopifyOrders = await getBasicShopifyOrderFront([shopifyOrderId])
			lastBox = shopifyOrders?.find((order) => order.id.includes(shopifyOrderId)) || null
		}
	}

	// Calculate total orders for this subscription
	const numOrders = subscriptionOrders?.length || 0

	// Determine customization type based on number of orders
	let customizationType: CustomizationType = 'NONE'
	if (numOrders === 1) {
		customizationType = 'NMC' // New Member Customization
	} else if (numOrders > 1) {
		customizationType = 'RMC' // Returning Member Customization
	}

	// Detect dunning recovery: if the latest successful charge for this subscription
	// required more than one attempt, the customer went through dunning (failed payment
	// retries). These customers get a 72-hour customization window bypassing the Prismic window.
	let isDunning = false
	if (customizationType === 'RMC') {
		const latestCharge = await getLastCharge(rechargeSession, subscription.id)
		isDunning = (latestCharge?.charge_attempts ?? 1) > 1
	}

	// Create a revalidation function specific to this subscription
	const revalidate = async () => {
		await globalMutate(`/subscriptions/${accessToken}`)
	}

	// Check if this is a prepaid cancelled subscription with pending orders
	let isEndingSoon = false

	/**
	 * Calculate remaining boxes in the subscription based on orders shipped.
	 * For prepaid subscriptions, charge_interval_frequency represents the total number of boxes.
	 *
	 * The calculation works as follows:
	 * 1. First, calculate base remaining boxes using modulo operation:
	 *    remainingBoxes = charge_interval_frequency - (numOrders % charge_interval_frequency)
	 *    This gives us the number of boxes left in the current interval
	 *
	 * 2. Then, handle special cases where we need to check customization:
	 *    - When we've shipped all boxes (remainingBoxes === charge_interval_frequency)
	 *    - When it's a single-box subscription (charge_interval_frequency === 1)
	 *    In these cases, remainingBoxes is set to 0 if the last box is customized, 1 if not
	 *
	 * 3. For prepaid cancelled subscriptions, we also determine if they're ending soon:
	 *    A subscription is ending soon if:
	 *    - It's cancelled AND
	 *    - It's prepaid AND
	 *    - Either there are remaining boxes OR the last box isn't customized yet
	 */
	let remainingBoxes = subscription.charge_interval_frequency
		? subscription.charge_interval_frequency - (numOrders % subscription.charge_interval_frequency)
		: 0

	// If we've shipped all boxes (modulo returns charge_interval_frequency) or it's a single-box subscription,
	// check if the last box is customized
	if (
		remainingBoxes === subscription.charge_interval_frequency ||
		subscription.charge_interval_frequency === 1
	) {
		// Determine which customization tag to check based on order count
		const customizationTag = numOrders === 1 ? 'box_customized' : 'rmc_box_customized'
		const isLastBoxCustomized = lastBox?.tags.includes(customizationTag)
		// If the last box is customized, we have 0 remaining boxes
		// If not, we still have 1 box to customize
		remainingBoxes = isLastBoxCustomized ? 0 : 1
	}

	// For prepaid cancelled subscriptions, check if it's ending soon
	if (subscription.is_prepaid && subscription.status === 'cancelled' && lastBox) {
		// Determine which customization tag to check based on order count
		const customizationTag = numOrders === 1 ? 'box_customized' : 'rmc_box_customized'
		const isLastBoxCustomized = lastBox.tags.includes(customizationTag)
		// A subscription is ending soon if there are remaining boxes or the last box isn't customized
		isEndingSoon = remainingBoxes > 0 || !isLastBoxCustomized
	}

	return {
		...subscription,
		address: subscription.address,
		status: subscription.status,
		numOrders,
		orders: subscriptionOrders,
		lastBox,
		customizationType,
		isDunning,
		revalidate,
		isEndingSoon,
		remainingBoxes
	} as SubscriptionWithDetails
}

/**
 * Hook to manage and fetch subscription data
 *
 * This hook fetches all subscriptions at once and processes their details in parallel.
 * Each subscription gets its own revalidate function that triggers a refresh of all
 * subscription data, ensuring consistency across the application.
 *
 * @returns {Object} An object containing:
 * - subscriptions: Array of all subscriptions with their status and order details
 * - loading: Whether the data is currently being fetched
 * - subscriptionLoading: Whether the session is still being initialized
 * - revalidateAll: Function to manually refresh all subscription data
 * - error: Any error that occurred during data fetching
 */
export default function useSubscriptions() {
	const { data: session } = useSession()
	const accessToken = (session as any)?.shopifyAccessToken

	const {
		data: subscriptions,
		isValidating: loading,
		mutate: revalidateAll,
		error
	} = useSWR<SubscriptionWithDetails[] | null>(
		accessToken ? `/subscriptions/${accessToken}` : null,
		async () => {
			try {
				const rechargeSession = await loginWithShopify(accessToken)
				if (!rechargeSession) return null

				// Fetch all subscriptions with their addresses in one call
				const rechargeSubscriptions = await getRCSubscriptions(rechargeSession, true)
				if (!rechargeSubscriptions) return null

				// Process each subscription's details in parallel
				const processedSubscriptions = await Promise.all(
					rechargeSubscriptions.map((sub) =>
						processSubscriptionDetails(
							rechargeSession,
							{ ...sub, address: sub.include!.address! },
							accessToken!
						)
					)
				)

				// Move cancelled subscriptions with pending orders (ending soon) to active section
				const sortedSubscriptions = processedSubscriptions.sort((a, b) => {
					// Active subscriptions first
					if (a.status === 'active' && b.status !== 'active') return -1
					if (a.status !== 'active' && b.status === 'active') return 1

					// Then ending soon subscriptions
					if (a.isEndingSoon && !b.isEndingSoon) return -1
					if (!a.isEndingSoon && b.isEndingSoon) return 1

					return 0
				})

				return sortedSubscriptions
			} catch (err) {
				console.error('Error fetching subscriptions:', err)
				Sentry.captureException(err)
				return null
			}
		},
		{
			revalidateIfStale: true,
			revalidateOnMount: true
		}
	)

	const subscriptionLoading = session === undefined

	// Extract active and cancelled subscriptions
	const activeSubscriptions =
		subscriptions?.filter((sub) => sub.status === 'active' || sub.isEndingSoon) || []
	const cancelledSubscriptions =
		subscriptions?.filter(
			(sub) => (sub.status === 'cancelled' || sub.status === 'expired') && !sub.isEndingSoon
		) || []

	return {
		/** Array of all subscriptions with their status and order details */
		subscriptions,
		/** Array of active subscriptions including those ending soon */
		activeSubscriptions,
		/** Array of cancelled subscriptions excluding those ending soon */
		cancelledSubscriptions,
		/** Whether the data is currently being fetched */
		loading,
		/** Whether the session is still being initialized */
		subscriptionLoading,
		/** Function to manually refresh all subscription data */
		revalidateAll,
		/** Any error that occurred during data fetching */
		error
	}
}
